import {
  DvIconCellRendererParams,
  DvIconHeaderRendererParams,
  DvToolbarAction,
} from '@devinco/ui/grid';
import { Injectable, inject } from '@angular/core';
import { ColumnType } from '@features/vt-grid/grid-options/column-types';
import { vtPresetActions } from '@features/vt-grid/presets/preset-actions';
import { TranslateService } from '@ngx-translate/core';
import { OnCallStatus } from '@pages/on-call-page/constants';
import { OnCallDto } from '@pages/on-call-page/interfaces';
import { ColDef, GridApi } from 'ag-grid-community';

@Injectable({
  providedIn: 'root',
})
export class OnCallGridService {
  private translate = inject(TranslateService);

  gridApi: GridApi<OnCallDto>;

  getColumnDefs = (): ColDef<OnCallDto>[] => [
    {
      headerName: 'Grid.Id',
      field: 'ID',
      type: [ColumnType.Id],
      enableRowGroup: false,
      hide: true,
    },
    {
      field: 'IsVehicle',
      headerName: 'OnCallGrid.IsVehicle',
      type: [ColumnType.Icon],
      cellRendererParams: {
        icon: 'car',
      } as DvIconCellRendererParams,
      headerComponentParams: {
        icon: 'car',
        tooltip: this.translate.instant('OnCallGrid.IsVehicle'),
      } as DvIconHeaderRendererParams,
      enableRowGroup: true,
      valueGetter: ({ data }) => {
        return data?.IsVehicle;
      },
      filterParams: {
        values: [1, 0],
        valueFormatter: (data: { value: boolean }) => {
          const key = data.value
            ? 'OnCallGrid.IsVehicle'
            : 'OnCallGrid.IsNotVehicle';
          return this.translate.instant(key);
        },
      },
      sortable: true,
      filter: 'agSetColumnFilter',
    },
    {
      field: 'IsLocation',
      headerName: 'OnCallGrid.IsLocation',
      type: [ColumnType.Icon],
      cellRendererParams: (params: { data: OnCallDto | undefined }) => {
        return {
          icon: 'vt_map_standby',
          color: this.getOnCallStatusIconColor(params.data?.OnCallStatus),
          value: !!params.data?.OnCallStatus,
        };
      },
      headerComponentParams: {
        icon: 'vt_map_standby',
        tooltip: this.translate.instant('OnCallGrid.IsLocation'),
      } as DvIconHeaderRendererParams,
      enableRowGroup: true,
      valueGetter: ({ data }) => {
        return data?.IsLocation;
      },
      filterParams: {
        values: [1, 0],
        valueFormatter: (data: { value: boolean }) => {
          const key = data.value
            ? 'OnCallGrid.IsLocation'
            : 'OnCallGrid.IsNotLocation';
          return this.translate.instant(key);
        },
      },
      sortable: true,
      filter: 'agSetColumnFilter',
    },
    {
      field: 'OnCallStatus',
      headerName: 'OnCallGrid.OnCallStatus',
      type: [ColumnType.TranslatedText],
      valueGetter: ({ data }) => data?.OnCallStatus,
      filterParams: {
        values: Object.values(OnCallStatus),
        valueFormatter: (data: { value: OnCallStatus | undefined }) =>
          this.translate.instant(`OnCallStatus.${data.value}`),
      },
      valueFormatter: params =>
        params.data?.OnCallStatus
          ? this.translate.instant(`OnCallStatus.${params.data.OnCallStatus}`)
          : '',
      filter: 'agSetColumnFilter',
    },
    {
      field: 'Description',
      headerName: 'OnCallGrid.Description',
      type: [ColumnType.Text],
      filterParams: {
        maxNumConditions: 100,
      },
    },
    {
      field: 'Username',
      headerName: 'OnCallGrid.Username',
      type: [ColumnType.Text],
    },
    {
      field: 'UserDescription',
      headerName: 'OnCallGrid.UserDescription',
      type: [ColumnType.Text],
    },
    {
      field: 'EmployeeNumber',
      headerName: 'OnCallGrid.EmployeeNumber',
      type: [ColumnType.Text],
    },
    {
      field: 'UserTag',
      headerName: 'OnCallGrid.UserTag',
      type: [ColumnType.Text],
      filterParams: {
        maxNumConditions: 100,
      },
    },
    {
      field: 'VehicleTag',
      headerName: 'OnCallGrid.VehicleTag',
      type: [ColumnType.Text],
      filterParams: {
        maxNumConditions: 100,
      },
    },
    {
      field: 'GPSLat',
      type: [ColumnType.Invisible],
    },
    {
      field: 'GPSLon',
      type: [ColumnType.Invisible],
    },
    {
      field: 'LastActivityTime',
      type: [ColumnType.Invisible],
    },
    { field: 'LastCourse', type: [ColumnType.Invisible] },
    { field: 'Mobile', type: [ColumnType.Invisible] },
    {
      field: 'DepartmentDescription',
      headerName: 'OnCallGrid.DepartmentDescription',
      type: [ColumnType.Text],
      filterParams: {
        maxNumConditions: 100,
      },
    },
    {
      field: 'DepartmentID',
      headerName: 'OnCallGrid.DepartmentID',
      hide: true,
      type: [ColumnType.Number],
      filterParams: {
        maxNumConditions: 100,
      },
    },
    {
      field: 'UserID',
      headerName: 'OnCallGrid.UserID',
      hide: true,
      type: [ColumnType.Number],
      filterParams: {
        maxNumConditions: 100,
      },
    },
    { field: 'CallOuts', type: [ColumnType.Invisible] },
    { field: 'OnCallSiteID', type: [ColumnType.Invisible] },
    { field: 'LastCallOutTime', type: [ColumnType.Invisible] },
    { field: 'CanBeCalledOut', type: [ColumnType.Invisible] },
  ];

  getGridActions(): DvToolbarAction[] {
    return [vtPresetActions.reset, vtPresetActions.fit, vtPresetActions.reload];
  }

  private getOnCallStatusIconColor(status: OnCallStatus | undefined): string {
    switch (status) {
      case OnCallStatus.OnCall:
        return 'var(--dv-bg-surface-success-active)';
      case OnCallStatus.Idle:
        return 'var(--dv-bg-surface-warning-active)';
      default:
      case OnCallStatus.NotOnCall:
        return '';
    }
  }
}
